// analytics.js — Google Analytics 4. Off unless REACT_APP_GA_MEASUREMENT_ID is
// set at build time (frontend/.env.production), and never in automated
// browsers (the pre-renderer and browser tests), so they don't count as visits.
//
// Consent Mode defaults are set by the inline script at the top of
// public/index.html: denied in the EEA, UK and Switzerland until the AdSense
// consent message (Google's CMP) updates them, granted elsewhere.
//
// Page views are sent by trackPageView() on every route change, including the
// first, so GA's own "page changes based on browser history events" setting
// must be off in the web stream's enhanced measurement (it would double count).
//
// The owner's own browsers: opening any page with ?internal=1 marks that
// browser (localStorage) and its hits carry traffic_type=internal, the value
// GA's "Internal Traffic" data filter matches (Testing tags them, Active
// drops them). It works on any network, unlike an IP rule. ?internal=0 undoes
// it. The parameter is taken out of the address bar at once, so a shared
// link can't mark anyone else.

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const enabled = Boolean(GA_ID) && typeof window !== "undefined" && !navigator.webdriver;
const INTERNAL_KEY = "pl-gami-internal";

// The first page view (and anything else) can come before initAnalytics(),
// which waits for the page's load event. gtag drops an event queued ahead of
// its config, so these wait here and go out right after it: until
// 2026-09-28 about half of all visits had a "(not set)" landing page.
let ready = false;
const waiting = [];
const send = (name, params) => (ready ? gtag("event", name, params) : waiting.push([name, params]));

// Called once at startup (index.js), before the router reads the URL.
export function rememberInternalBrowser() {
  try {
    const url = new URL(window.location.href);
    const flag = url.searchParams.get("internal");
    if (flag === null) return;
    if (flag === "1") localStorage.setItem(INTERNAL_KEY, "1");
    if (flag === "0") localStorage.removeItem(INTERNAL_KEY);
    url.searchParams.delete("internal");
    window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
  } catch {
    // no storage (private mode): counted as usual
  }
}

const isInternalBrowser = () => {
  try {
    return localStorage.getItem(INTERNAL_KEY) === "1";
  } catch {
    return false;
  }
};

function gtag() {
  // Defined by public/index.html; fall back so a missing snippet can't throw.
  if (!window.gtag) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
  }
  window.gtag(...arguments);
}

export function initAnalytics() {
  if (!enabled) return;
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);
  gtag("js", new Date());
  const internal = isInternalBrowser() ? { traffic_type: "internal" } : {};
  if (internal.traffic_type) gtag("set", internal);
  gtag("config", GA_ID, { send_page_view: false, ...internal });
  ready = true;
  waiting.splice(0).forEach(([name, params]) => gtag("event", name, params));
}

export function trackPageView() {
  if (!enabled) return;
  // React inserts the page's <title> ahead of index.html's fallback, so
  // document.title is the page's own title, as in the browser tab.
  send("page_view", { page_location: window.location.href, page_title: document.title });
}

export function trackEvent(name, params) {
  if (!enabled) return;
  send(name, params);
}

// Clicks out to Buy Me a Coffee and @footygami on X, wherever the link sits
// (header, footer, pages): one listener rather than a handler on every link.
// And errors visitors hit (three at most per page load), so a broken page
// shows up in the Monday email before anyone reports it.
export function watchClicksAndErrors() {
  if (!enabled) return;
  document.addEventListener(
    "click",
    (e) => {
      const a = e.target.closest && e.target.closest("a[href]");
      const href = a ? a.getAttribute("href") : "";
      const method = /buymeacoffee\.com/.test(href) ? "coffee" : /x\.com\/footygami/.test(href) ? "x_follow" : null;
      if (method) send("outbound_click", { method, content_type: window.location.pathname });
    },
    true
  );
  let errors = 0;
  const report = (message) => {
    if (!message || errors >= 3 || /ResizeObserver loop|^Script error\.?$/i.test(message)) return;
    errors += 1;
    send("js_error", { item_id: String(message).slice(0, 100), content_type: window.location.pathname });
  };
  window.addEventListener("error", (e) => report(e.message));
  window.addEventListener("unhandledrejection", (e) => report((e.reason && e.reason.message) || String(e.reason)));
}
